/**
 * GMA-1909 — "the session has ended" as an in-page signal.
 *
 * Sign-out has to reach surfaces that are NOT behind `RequireLogin`. The
 * standalone `/call` route is the one that matters: it renders a live call with
 * client data on screen, and it is mounted directly rather than wrapped by the
 * auth guard, so clearing the token does not unmount it. A tab that loses the
 * cross-tab expiry race would otherwise keep showing that call after the
 * session is over.
 *
 * It also has to reach a page that is BLOCKING navigation. The call page
 * registers a `beforeunload` handler that calls `preventDefault()` while a call
 * is active, so the redirect to /login raises a browser prompt the user can
 * cancel - leaving a signed-out session visible with no retry. A page that
 * hears this signal drops that guard and tears the call down, so the navigation
 * that follows cannot be refused.
 *
 * Deliberately not React context: the emitters are a storage event and a
 * logout() that runs outside render, and the listeners are pages that must
 * react even while unmounting.
 */
import { SESSION_END_TEARDOWN_MS } from "../config/sessionTimeout";

const listeners = new Set();

export const onSessionEnd = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Tell every surface the session has ended, and RESOLVE when they are done.
 *
 * Teardown is a network call - the call page POSTs `call/end` - and the
 * sign-out navigates immediately afterwards, which aborts an in-flight fetch
 * and leaves the call active on the server. Starting the request is not
 * finishing it, so a caller that is about to navigate awaits this.
 *
 * Returns a promise that never rejects: one surface failing to tear down must
 * never stop the others, and must never stop the sign-out itself.
 */
export const emitSessionEnd = () => {
  // Copy first: a listener that unsubscribes itself while ending a call would
  // otherwise mutate the set mid-iteration.
  const settled = [...listeners].map((listener) => {
    try {
      return Promise.resolve(listener());
    } catch (_err) {
      return Promise.resolve();
    }
  });
  return Promise.allSettled(settled);
};

/**
 * The same, with a ceiling. A surface that hangs must not hold a sign-out open
 * - the session is already over, and the redirect has to happen either way.
 */
export const emitSessionEndAndSettle = (timeoutMs = SESSION_END_TEARDOWN_MS) =>
  Promise.race([
    emitSessionEnd(),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);

export const __resetSessionEndListenersForTests = () => listeners.clear();
